import { useEffect, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { HiOutlineUserGroup } from 'react-icons/hi'

import {
  Box,
  Checkbox,
  CheckboxGroup,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import {
  ManagedAccountsDocument,
  useUpdateUserRolesMutation,
  useUserQuery,
} from '@/graphql/generated/hooks'
import { getErrorMessage } from '@/utils/errorMessage'

import { ROLE_OPTIONS } from '../types/types'
import { VALID_ROLE_VALUES, normalizeToRoleOption } from '../utils/helpers'

interface IProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  fullName: string
}

export const ChangeRolesModal = ({
  isOpen,
  onClose,
  userId,
  fullName,
}: IProps) => {
  const mode = useLightMode()
  const { shouldEnableUpdateUser } = usePermissions()
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [areRolesLoaded, setAreRolesLoaded] = useState(false)
  const { showSuccess, showError } = useToasts()

  const {
    data: userData,
    loading: isUserLoading,
    error: userQueryError,
  } = useUserQuery({
    variables: { id: userId },
    fetchPolicy: 'network-only',
  })

  const [updateUserRoles, { loading: isUpdating }] =
    useUpdateUserRolesMutation()

  // Reset state when userId changes
  useEffect(() => {
    setAreRolesLoaded(false)
    setSelectedRoles([])
  }, [userId])

  // Load existing roles from user data
  useEffect(() => {
    if (userData?.user && !areRolesLoaded) {
      const userRoles = userData.user.roles
      if (userRoles && userRoles.length > 0) {
        const normalizedRoles = userRoles
          .map((r: string) => normalizeToRoleOption(r))
          .filter((r): r is NonNullable<typeof r> => r !== null)
        setSelectedRoles(normalizedRoles)
      } else if (userData.user.roleName) {
        const normalized = normalizeToRoleOption(userData.user.roleName)
        if (normalized) {
          setSelectedRoles([normalized])
        }
      }
      setAreRolesLoaded(true)
    }
  }, [userData, areRolesLoaded])

  const handleSubmit = async () => {
    // Filter to only valid roles from ROLE_OPTIONS
    const validRoles = selectedRoles.filter((role) =>
      VALID_ROLE_VALUES.includes(role)
    )

    if (validRoles.length === 0) {
      showError('Please select at least one role')
      return
    }

    try {
      await updateUserRoles({
        variables: {
          input: {
            id: userId,
            roles: validRoles,
          },
        },
        refetchQueries: [{ query: ManagedAccountsDocument }],
      })
      onClose()
      showSuccess(`Roles updated for ${fullName}`)
    } catch (error) {
      showError(getErrorMessage(error))
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='md'
    >
      <ModalOverlay />
      <ModalContent bg={`bg.card-elevated.${mode}`}>
        <ModalHeader>
          <Box alignItems='center' display='flex'>
            <Icon
              as={HiOutlineUserGroup}
              boxSize='30px'
              color={`btn.change-roles.${mode}`}
            />
            <Box ml={3}>
              <Box
                color={`text.primary.${mode}`}
                fontSize='22px'
                fontWeight='extrabold'
                letterSpacing='-0.4px'
                lineHeight='1'
              >
                Change Roles
              </Box>
              <Box color={`text.secondary.${mode}`} fontSize='sm' mt={1}>
                {fullName}
              </Box>
            </Box>
          </Box>
        </ModalHeader>
        <ModalCloseButton color={`text.primary.${mode}`} opacity={0.4} />
        <ModalBody>
          {isUserLoading ? (
            <Box>
              <Skeleton h='20px' mb={3} w='100px' />
              <Stack spacing={2}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton h='24px' key={i} w='150px' />
                ))}
              </Stack>
            </Box>
          ) : userQueryError ? (
            <Box color='red.500' data-testid='ChangeRolesModal:error'>
              Failed to load user roles. Please try again.
            </Box>
          ) : userData?.user ? (
            <Box>
              <Box
                color={`text.primary.${mode}`}
                fontSize='14px'
                fontWeight='semibold'
                mb={3}
              >
                Select roles for this user
              </Box>
              <CheckboxGroup
                colorScheme='blue'
                onChange={(values) => setSelectedRoles(values as string[])}
                value={selectedRoles}
              >
                <Stack spacing={2}>
                  {ROLE_OPTIONS.map((option) => (
                    <Checkbox
                      data-testid={`ChangeRolesModal:role:${option.value}`}
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
            </Box>
          ) : null}
        </ModalBody>
        <ModalFooter py={4}>
          <Button
            data-testid='ChangeRolesModal:cancel'
            mr={3}
            onClick={onClose}
            variant='secondary'
          >
            Cancel
          </Button>
          <Button
            data-testid='ChangeRolesModal:submit'
            isDisabled={
              isUserLoading ||
              !!userQueryError ||
              !userData?.user ||
              !shouldEnableUpdateUser ||
              selectedRoles.length === 0
            }
            isLoading={isUpdating}
            loadingText='Saving...'
            onClick={handleSubmit}
          >
            Save Roles
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
